import React, { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import { VyberVyrobce, VyberModel, useCenikVyber } from "./VyberZCeniku";
import api, { apiError } from "../lib/api";
import { fmtYmd } from "../lib/servis";
import Vyber from "./Vyber";

// Založení / úprava zařízení v péči. `clientId` se předává z karty klienta.
// Výrobce a model se vybírají z ceníku — co tam není, se musí nejdřív doplnit.
export default function ZarizeniForm({ zarizeni = null, clientId = null, onClose, onSaved, onDeleted }) {
  const editing = !!zarizeni;
  const { vyrobci, zarizeni: modely } = useCenikVyber();
  const [kats, setKats] = useState([]);
  const [f, setF] = useState({
    client_id: zarizeni?.client_id || clientId || "",
    kategorie_id: zarizeni?.kategorie_id || "",
    nazev: zarizeni?.nazev || "",
    vyrobce: zarizeni?.vyrobce || "",
    vyrobce_id: zarizeni?.vyrobce_id || null,
    model: zarizeni?.model || "",
    polozka_id: zarizeni?.polozka_id || null,
    vykon: zarizeni?.vykon || "",
    vyrobni_cislo: zarizeni?.vyrobni_cislo || "",
    popis: zarizeni?.popis || "",
    datum_zprovozneni: zarizeni?.datum_zprovozneni || "",
    zaruka_do: zarizeni?.zaruka_do || "",
    servisni_interval_mesice: zarizeni?.servisni_interval_mesice ?? 12,
    // Další prohlídka = událost v kalendáři (datum + čas). Prázdné = podle intervalu.
    prohlidka_datum: zarizeni?.planovana_prohlidka?.datum || "",
    prohlidka_cas: zarizeni?.planovana_prohlidka?.cas || "08:00",
    adresa_instalace: { ...(zarizeni?.adresa_instalace || { ulice: "", psc: "", mesto: "" }) },
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/meta/ciselniky").then((r) => setKats(r.data.kategorie || [])).catch(() => {});
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const updA = (k) => (e) => setF({ ...f, adresa_instalace: { ...f.adresa_instalace, [k]: e.target.value } });

  // Změna technologie nebo výrobce: model, který k nim nepatří, se odvybere.
  const zmenKategorii = (kid) => {
    const v = vyrobci.find((x) => x.id === f.vyrobce_id);
    const vyrobceZustava = !kid || !v || (v.kategorie_ids || []).includes(kid);
    setF({ ...f, kategorie_id: kid,
      ...(vyrobceZustava ? {} : { vyrobce_id: null, vyrobce: "", polozka_id: null, model: "" }) });
  };
  const zmenVyrobce = ({ vyrobce_id, vyrobce }) => {
    setF({ ...f, vyrobce_id, vyrobce, polozka_id: null, model: "" });
  };
  // Název zařízení se předvyplní z modelu, pokud ho uživatel ještě nepřepsal.
  const zmenModel = (z) => {
    const nazevZModelu = !f.nazev || f.nazev === f.model;
    setF({ ...f, polozka_id: z?.id || null, model: z?.name || "",
      kategorie_id: f.kategorie_id || z?.kategorie_id || "",
      nazev: nazevZModelu ? (z?.name || "") : f.nazev });
  };

  const smazat = async () => {
    if (!window.confirm(`Opravdu smazat zařízení „${zarizeni.nazev}“? Naplánované výjezdy k němu se zruší, servisní historie zůstane.`)) return;
    setError(""); setSaving(true);
    try {
      await api.delete(`/api/zarizeni/${zarizeni.id}`);
      onClose();
      onDeleted && onDeleted();
    } catch (err2) {
      setError(apiError(err2.response?.data?.detail) || err2.message);
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_id) return setError("Chybí klient.");
    if (!f.vyrobce_id) return setError("Vyber výrobce z ceníku.");
    if (!f.polozka_id) return setError("Vyber model z ceníku.");
    if (!f.nazev.trim()) return setError("Zadej název zařízení.");
    setError(""); setSaving(true);
    const body = {
      ...f,
      kategorie_id: f.kategorie_id || null,
      servisni_interval_mesice: Number(f.servisni_interval_mesice) || 0,
      datum_zprovozneni: f.datum_zprovozneni || null,
      zaruka_do: f.zaruka_do || null,
    };
    // Termín prohlídky se posílá, jen když se změnil — jinak by se naplánovaná
    // prohlídka v kalendáři zbytečně přepisovala.
    const puvodni = zarizeni?.planovana_prohlidka;
    const zmena = f.prohlidka_datum !== (puvodni?.datum || "")
      || (f.prohlidka_datum && f.prohlidka_cas !== (puvodni?.cas || "08:00"));
    if (!zmena) { delete body.prohlidka_datum; delete body.prohlidka_cas; }
    try {
      let res;
      if (editing) res = await api.patch(`/api/zarizeni/${zarizeni.id}`, body);
      else res = await api.post("/api/zarizeni", body);
      onSaved && onSaved(res.data);
      onClose();
    } catch (err2) {
      setError(apiError(err2.response?.data?.detail) || err2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="zarizeni-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[560px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-line-softer px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-[16px] font-800 text-ink">{editing ? "Upravit zařízení" : "Nové zařízení"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="zarizeni-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Technologie">
            <Vyber className="field" value={f.kategorie_id} onChange={(e) => zmenKategorii(e.target.value)} data-testid="zarizeni-kategorie">
              <option value="">Vyber technologii…</option>
              {kats.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </Vyber>
          </Field>
          <Field label="Výrobce">
            <VyberVyrobce vyrobci={vyrobci} kategorieId={f.kategorie_id} vyrobceId={f.vyrobce_id}
              onChange={zmenVyrobce} testid="zarizeni-vyrobce" />
            {editing && !f.vyrobce_id && f.vyrobce && (
              <div className="t-meta text-muted mt-1">Dřív zadáno ručně: {f.vyrobce}. Vyber výrobce z ceníku.</div>
            )}
          </Field>
          <Field label="Model">
            <VyberModel zarizeni={modely} vyrobceId={f.vyrobce_id} kategorieId={f.kategorie_id} polozkaId={f.polozka_id}
              onChange={zmenModel} testid="zarizeni-model" />
            {editing && !f.polozka_id && f.model && (
              <div className="t-meta text-muted mt-1">Dřív zadáno ručně: {f.model}. Vyber model z ceníku.</div>
            )}
          </Field>
          <Field label="Název zařízení" hint="Předvyplní se z modelu, jde přepsat, třeba na Rekuperace 1. patro.">
            <input className="field" value={f.nazev} onChange={upd("nazev")} placeholder="Viessmann Vitocal 250-A" data-testid="zarizeni-nazev" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Výkon"><input className="field" value={f.vykon} onChange={upd("vykon")} placeholder="12 kW" /></Field>
            <Field label="Výrobní číslo"><input className="field amount" value={f.vyrobni_cislo} onChange={upd("vyrobni_cislo")} data-testid="zarizeni-vyrobni-cislo" /></Field>
          </div>
          <Field label="Popis"><input className="field" value={f.popis} onChange={upd("popis")} placeholder="vzduch/voda" /></Field>
          {/* Pořadí podle života zařízení: zprovoznění → další prohlídka → konec záruky. */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.35fr_1fr] gap-3">
            <Field label="V provozu od">
              <input type="date" className="field min-w-0" value={f.datum_zprovozneni} onChange={upd("datum_zprovozneni")} data-testid="zarizeni-zprovozneni" />
            </Field>
            <Field label="Další prohlídka"
              hint={f.prohlidka_datum ? "Zapíše se do kalendáře."
                : zarizeni?.dalsi_prohlidka && Number(f.servisni_interval_mesice) > 0
                  ? `Podle intervalu ${fmtYmd(zarizeni.dalsi_prohlidka)}. Vyplň a zapíše se do kalendáře.`
                  : "Vyplň a zapíše se do kalendáře."}>
              <div className="flex gap-2">
                <input type="date" className="field min-w-0 flex-1" value={f.prohlidka_datum} onChange={upd("prohlidka_datum")} data-testid="zarizeni-prohlidka-datum" />
                <input type="time" step="900" className="field min-w-0 w-[96px] shrink-0 amount" value={f.prohlidka_cas} onChange={upd("prohlidka_cas")}
                  disabled={!f.prohlidka_datum} data-testid="zarizeni-prohlidka-cas" />
              </div>
            </Field>
            <Field label="Záruka do">
              <input type="date" className="field min-w-0" value={f.zaruka_do} onChange={upd("zaruka_do")} data-testid="zarizeni-zaruka" />
            </Field>
          </div>
          <Field label="Prohlídka každých (měsíců)" hint="Z intervalu se počítá další termín po každé prohlídce. 0 = bez servisu.">
            <input type="number" min="0" max="120" className="field amount sm:max-w-[160px]" value={f.servisni_interval_mesice} onChange={upd("servisni_interval_mesice")} data-testid="zarizeni-interval" />
          </Field>
          <div className="grid grid-cols-[1fr_120px_1fr] gap-3">
            <Field label="Ulice"><input className="field" value={f.adresa_instalace.ulice} onChange={updA("ulice")} /></Field>
            <Field label="PSČ"><input className="field amount" value={f.adresa_instalace.psc} onChange={updA("psc")} /></Field>
            <Field label="Město"><input className="field" value={f.adresa_instalace.mesto} onChange={updA("mesto")} /></Field>
          </div>
          {error && <div className="text-[12.5px] text-danger" data-testid="zarizeni-form-error">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <button type="button" onClick={smazat} disabled={saving} data-testid="zarizeni-smazat"
                className="tap mr-auto inline-flex items-center gap-1.5 t-meta font-700 text-danger hover:underline disabled:opacity-50">
                <Trash2 size={15} /> Smazat
              </button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>Zrušit</Button>
            <Button type="submit" disabled={saving} data-testid="zarizeni-form-submit">
              {saving ? <Spinner /> : editing ? "Uložit" : "Přidat zařízení"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
